import React from 'react';
import * as css from './flags.module.scss';
import classNames from 'classnames';
import { Language } from '../../i18n/LanguageStore';

export enum FlagTypes {
  NO,
  UK,
}

interface FlagComponentInterface {
  className?: string;
  title?: string;
  highlighted?: boolean;
  onChange?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

interface FlagInterface {
  flag: FlagTypes;
  className?: string;
  highlighted?: boolean;
  onChange?: (
    event: React.MouseEvent | React.KeyboardEvent,
    value: FlagTypes,
  ) => void;
}

interface FlagWrapperInterface {
  flags: FlagTypes[];
  className?: string;
  flagClassName?: string;
  highlighted?: FlagTypes;
  label?: string;
  onChange?: (
    event: React.MouseEvent | React.KeyboardEvent,
    value: FlagTypes,
  ) => void;
}

export const getFlagFromLanguage = (lang: Language): FlagTypes => {
  switch (lang) {
    case Language.ENGLISH:
      return FlagTypes.UK;
    case Language.NORWEGIAN:
      return FlagTypes.NO;
  }
};

export const getLanguageFromFlagType = (flag: FlagTypes): Language => {
  switch (flag) {
    case FlagTypes.NO:
      return Language.NORWEGIAN;
    case FlagTypes.UK:
      return Language.ENGLISH;
  }
};

// Component for presenting flags
const FlagComponent: React.FC<
  React.PropsWithChildren<FlagComponentInterface>
> = ({ className, onChange, title, highlighted }) =>
  onChange !== undefined ? (
    <div
      className={classNames(css.flag, className)}
      onClick={(event) => {
        onChange(event);
      }}
      onKeyDown={(event) => {
        onChange(event);
      }}
      tabIndex={0}
      title={title}
      role={'button'}
      aria-pressed={highlighted}
    />
  ) : (
    <div className={classNames(css.flag, className)} title={title} />
  );

// Individual flag components
const UK: React.FC<React.PropsWithChildren<FlagComponentInterface>> = ({
  className,
  onChange,
  highlighted,
}) => (
  <FlagComponent
    title={'English'}
    className={classNames(css.uk, className)}
    onChange={onChange}
    highlighted={highlighted}
  />
);

const NO: React.FC<React.PropsWithChildren<FlagComponentInterface>> = ({
  className,
  onChange,
  highlighted,
}) => (
  <FlagComponent
    title={'Norsk'}
    className={classNames(css.no, className)}
    onChange={onChange}
    highlighted={highlighted}
  />
);

// Getter for individual flag components based on flag type
const getFlag = (flagParams: FlagInterface) => {
  const { flag, className, onChange, highlighted } = flagParams;

  const onClickHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (onChange) {
      const keyDown = (event as React.KeyboardEvent).key;
      if (keyDown === 'Enter' || !keyDown) onChange(event, flag);
    }
  };

  switch (flag) {
    case FlagTypes.UK:
      return (
        <UK
          className={className}
          onChange={onClickHandler}
          highlighted={highlighted}
        />
      );
    case FlagTypes.NO:
      return (
        <NO
          className={className}
          onChange={onClickHandler}
          highlighted={highlighted}
        />
      );
  }
};

// Components for flag types
const Flag: React.FC<React.PropsWithChildren<FlagInterface>> = ({
  flag,
  className,
  onChange,
  highlighted,
}) => getFlag({ flag, className, onChange: onChange, highlighted });

const FlagWrapper: React.FC<React.PropsWithChildren<FlagWrapperInterface>> = ({
  flags,
  className,
  flagClassName,
  onChange,
  highlighted,
  label,
}) => (
  <div className={classNames(css.flagWrapper, className)} aria-label={label}>
    {flags.map((flag) => (
      <Flag
        key={flag}
        flag={flag}
        className={classNames(
          flagClassName,
          flag === highlighted ? css.highlighted : '',
        )}
        highlighted={flag === highlighted}
        onChange={onChange}
      />
    ))}
  </div>
);

export default FlagWrapper;
